import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function Phase3_5PrimaryPurpose({ wizardData, setWizardData, onNext, onPrev }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-lg font-semibold mb-2 block">Primary Purpose Determination</Label>
        <p className="text-sm text-slate-500 mb-2">
          Since you selected <span className="font-semibold text-emerald-600">Mixed (Combination)</span> procurement, please identify which category represents the PRIMARY purpose.
        </p>
        <div className="mt-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
          <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
            <strong>Instructions:</strong> Identify which category represents the largest portion of the contract value or scope.
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-400">
            The Procuring Entity can best determine based on its <strong>primary purpose, motivation and intention</strong>, and on the <strong>nature of the contract</strong>, whether the procurement may be classified as Goods, Infrastructure Projects or Consulting Services.
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
            <em>For general support services under Goods:</em> The services must be &ldquo;essential, indispensable, or necessary to support the operations of any PE or for the enhancement of the welfare of its personnel.&rdquo;
          </p>
        </div>
        
        <RadioGroup 
          value={wizardData.primaryPurpose || ''}
          onValueChange={(value) => setWizardData({ ...wizardData, primaryPurpose: value })}
        >
          <div className="space-y-3">
            <div className="border rounded-lg p-4 hover:border-emerald-500 transition-all">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="goods" id="primary-goods" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="primary-goods" className="cursor-pointer font-medium block mb-1">
                    Primary: Goods
                  </Label>
                  <p className="text-xs text-slate-500">
                    The largest portion is goods/supplies, with services as secondary/support
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 hover:border-emerald-500 transition-all">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="infrastructure" id="primary-infrastructure" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="primary-infrastructure" className="cursor-pointer font-medium block mb-1">
                    Primary: Infrastructure
                  </Label>
                  <p className="text-xs text-slate-500">
                    The largest portion is construction/infrastructure work
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 hover:border-emerald-500 transition-all">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="consulting" id="primary-consulting" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="primary-consulting" className="cursor-pointer font-medium block mb-1">
                    Primary: Consulting Services
                  </Label>
                  <p className="text-xs text-slate-500">
                    The largest portion is consulting/professional services
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 hover:border-amber-500 transition-all bg-amber-50 dark:bg-amber-900/20">
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="unable" id="primary-unable" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="primary-unable" className="cursor-pointer font-medium block mb-1">
                    Unable to Determine
                  </Label>
                  <p className="text-xs text-slate-500">
                    Need clarification on contract breakdown (consult with BAC/Legal)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </RadioGroup>
        
        {wizardData.primaryPurpose === 'unable' && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-300 font-semibold mb-2">
              🛑 Recommended Action:
            </p>
            <p className="text-xs text-red-700 dark:text-red-400 mb-2">
              Request a contract breakdown from the end-user unit or project proponent. Consult with your BAC Secretariat and Legal Office to properly classify the procurement based on contract value distribution and primary deliverables.
            </p>
            <p className="text-xs text-red-800 dark:text-red-300 font-semibold">
              The wizard cannot proceed without determining the primary purpose. Please restart after clarification.
            </p>
          </div>
        )}
      </div>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} size="lg">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        {/* Hide Next button if "Unable to Determine" is selected */}
        {wizardData.primaryPurpose !== 'unable' && (
          <Button 
            onClick={onNext} 
            className="flex-1 bg-emerald-600 hover:bg-emerald-700" 
            size="lg"
            disabled={!wizardData.primaryPurpose}
          >
            Next <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
